import { useState, useEffect, type FormEvent } from 'react';
import { X, Terminal, ChevronDown, ChevronUp, BookOpen, Sparkles } from 'lucide-react';
import { useAgents, useAllAgents } from '../hooks/useAgents';
import { useCreateInstance, useCreateGlobalInstance, useSpawnInstance } from '../hooks/useInstances';
import { useRunners } from '../hooks/useRunners';
import { useSshHosts } from '../hooks/useSshHosts';
import { usePresets } from '../hooks/usePresets';
import { useAgentSkills } from '../hooks/useAgentSkills';
import { SpawnMode } from '../types/instance';
import type { CreateInstanceDto } from '../types/instance';
import type { Preset } from '../types/preset';
import PresetPicker from './PresetPicker';
import AgentSkillPicker from './AgentSkillPicker';
import RunOnSelector, { getDefaultTarget } from './RunOnSelector';
import type { RunTarget } from './RunOnSelector';
import WorkDirInput, { saveRecentWorkDir } from './WorkDirInput';
import { MODELS, DEFAULT_MODEL } from '../lib/models';

interface InstanceLaunchModalProps {
  open: boolean;
  onClose: () => void;
  projectId?: string;
  initialAgentId?: string;
}

const PERMISSION_MODES = [
  { value: 'acceptEdits', label: 'Accept Edits' },
  { value: 'plan', label: 'Plan Mode (read-only)' },
  { value: 'bypassPermissions', label: 'Bypass Permissions' },
];

export default function InstanceLaunchModal({ open, onClose, projectId, initialAgentId }: InstanceLaunchModalProps) {
  const { data: projectAgents } = useAgents(projectId);
  const { data: allAgents } = useAllAgents();
  const { data: runners } = useRunners();
  const { data: sshHosts } = useSshHosts();
  const { data: presets } = usePresets(projectId);
  const { data: agentSkills } = useAgentSkills(projectId);
  const createProjectInstance = useCreateInstance(projectId ?? '');
  const createGlobalInstance = useCreateGlobalInstance();
  const spawnInstance = useSpawnInstance();

  const [showOptions, setShowOptions] = useState(false);
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [customPrompt, setCustomPrompt] = useState('');
  const [agentId, setAgentId] = useState('');
  const [model, setModel] = useState(DEFAULT_MODEL);
  const [runtime, setRuntime] = useState<string>('claude');
  const [maxTurns, setMaxTurns] = useState(200);
  const [spawnMode, setSpawnMode] = useState<string>(SpawnMode.SDK);
  const [permissionMode, setPermissionMode] = useState('acceptEdits');
  const [workingDirectory, setWorkingDirectory] = useState('');
  const [runnerId, setRunnerId] = useState('');
  const [sshHostId, setSshHostId] = useState('');
  const [autoRestart, setAutoRestart] = useState(false);
  const [maxRetries, setMaxRetries] = useState(0);
  const [cortexEnrich, setCortexEnrich] = useState(false);
  const [selectedSkillIds, setSelectedSkillIds] = useState<string[]>([]);
  const [runTarget, setRunTarget] = useState<RunTarget>({ mode: 'sdk' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Merge agents: project agents + global agents (deduplicated)
  const agents = (() => {
    const list = [...(projectAgents ?? [])];
    const ids = new Set(list.map((a) => a.id));
    for (const a of allAgents ?? []) {
      if (!ids.has(a.id)) list.push(a);
    }
    return list;
  })();

  const handleTargetChange = (target: RunTarget) => {
    setRunTarget(target);
    if (target.mode === 'sdk') {
      setSpawnMode(SpawnMode.SDK);
      setRunnerId('');
      setSshHostId('');
    } else if (target.mode === 'host') {
      const runner = (runners ?? []).find((r) => r.id === target.runnerId);
      const isLlm = runner?.capabilities?.includes('local-llm') ?? false;
      setSpawnMode(isLlm ? SpawnMode.LOCAL_LLM : SpawnMode.HOST);
      setRunnerId(target.runnerId);
      setSshHostId('');
    } else if (target.mode === 'ssh') {
      setSpawnMode(SpawnMode.SSH);
      setRunnerId('');
      setSshHostId(target.sshHostId);
    }
  };

  // Apply preset values to form fields
  const applyPreset = (preset: Preset | null) => {
    if (!preset) {
      setSelectedPresetId(null);
      return;
    }
    setSelectedPresetId(preset.id);
    if (preset.model) setModel(preset.model);
    if (preset.maxTurns != null) setMaxTurns(preset.maxTurns);
    if (preset.permissionMode) setPermissionMode(preset.permissionMode);
    if (preset.autoRestart != null) setAutoRestart(preset.autoRestart);
    if (preset.maxRetries != null) setMaxRetries(preset.maxRetries);
    // Note: preset.spawnMode intentionally not applied — RunOnSelector owns that
  };

  // Auto-populate defaults when agent changes
  useEffect(() => {
    if (!agentId) return;
    const agent = agents.find((a) => a.id === agentId);
    if (!agent) return;
    if (agent.defaultModel) setModel(agent.defaultModel);
    if (agent.maxTurnsDefault) setMaxTurns(agent.maxTurnsDefault);
    if (agent.defaultCwd) setWorkingDirectory(agent.defaultCwd);
  }, [agentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset on open and apply smart run target
  useEffect(() => {
    if (open) {
      setShowOptions(initialAgentId ? true : false);
      setSelectedPresetId(null);
      setCustomPrompt('');
      setAgentId(initialAgentId ?? '');
      setModel(DEFAULT_MODEL);
      setRuntime('claude');
      setMaxTurns(200);
      setPermissionMode('acceptEdits');
      setWorkingDirectory('');
      setAutoRestart(false);
      setMaxRetries(0);
      setCortexEnrich(false);
      setSelectedSkillIds([]);

      const defaultTarget = getDefaultTarget(runners ?? [], sshHosts ?? []);
      setRunTarget(defaultTarget);
      if (defaultTarget.mode === 'sdk') {
        setSpawnMode(SpawnMode.SDK);
        setRunnerId('');
        setSshHostId('');
      } else if (defaultTarget.mode === 'host') {
        const runner = (runners ?? []).find((r) => r.id === defaultTarget.runnerId);
        const isLlm = runner?.capabilities?.includes('local-llm') ?? false;
        setSpawnMode(isLlm ? SpawnMode.LOCAL_LLM : SpawnMode.HOST);
        setRunnerId(defaultTarget.runnerId);
        setSshHostId('');
      } else if (defaultTarget.mode === 'ssh') {
        setSpawnMode(SpawnMode.SSH);
        setRunnerId('');
        setSshHostId(defaultTarget.sshHostId);
      }
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const effectiveSpawnMode = cortexEnrich ? SpawnMode.CORTEX_ENRICHED : spawnMode;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const dto: CreateInstanceDto = {
        prompt: customPrompt || undefined,
        model,
        runtime,
        maxTurns,
        maxBudgetUsd: 0,
        spawnMode: effectiveSpawnMode as SpawnMode,
        permissionMode,
        workingDirectory: workingDirectory || undefined,
        autoRestart,
        maxRetries: autoRestart ? maxRetries : undefined,
      };
      if (selectedSkillIds.length > 0) dto.skillIds = selectedSkillIds;
      if (agentId) dto.agentId = agentId;
      if (runnerId) dto.runnerId = runnerId;
      if (sshHostId) dto.sshHostId = sshHostId;

      let instance;
      if (projectId) {
        instance = await createProjectInstance.mutateAsync(dto);
      } else {
        instance = await createGlobalInstance.mutateAsync(dto);
      }
      await spawnInstance.mutateAsync(instance.id);

      if (workingDirectory) saveRecentWorkDir(runTarget, workingDirectory);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  // Label for the config summary
  const runLabel =
    runTarget.mode === 'host'
      ? runTarget.runnerName || runTarget.hostname
      : runTarget.mode === 'ssh'
        ? runTarget.sshName
        : 'This server';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-xl border border-slate-200 bg-white shadow-lg">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-slate-900">Launch Instance</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable content */}
        <form onSubmit={handleSubmit} className="flex-1 space-y-4 overflow-y-auto p-6">
          {/* Prompt textarea */}
          <div>
            <label
              htmlFor="launch-prompt"
              className="mb-1.5 block text-sm font-medium text-slate-700"
            >
              What should Claude do?
            </label>
            <textarea
              id="launch-prompt"
              rows={3}
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
              placeholder="Enter a prompt for the agent (optional)..."
              autoFocus
            />
          </div>

          {/* Run on — always visible */}
          <RunOnSelector
            value={runTarget}
            onChange={handleTargetChange}
            runners={runners ?? []}
            sshHosts={sshHosts ?? []}
          />

          {/* Working directory — always visible */}
          <WorkDirInput
            value={workingDirectory}
            onChange={setWorkingDirectory}
            target={runTarget}
          />

          {/* Preset Picker */}
          {presets && presets.length > 0 && (
            <PresetPicker
              presets={presets}
              selectedId={selectedPresetId}
              onSelect={applyPreset}
            />
          )}

          {/* Agent Skills */}
          {agentSkills && agentSkills.length > 0 && (
            <div>
              <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">
                <Sparkles className="h-3 w-3" />
                Skills
              </div>
              <AgentSkillPicker
                skills={agentSkills}
                selectedIds={selectedSkillIds}
                onToggle={(id) =>
                  setSelectedSkillIds((prev) =>
                    prev.includes(id)
                      ? prev.filter((s) => s !== id)
                      : [...prev, id],
                  )
                }
              />
            </div>
          )}

          {/* Show/hide options toggle */}
          <button
            type="button"
            onClick={() => setShowOptions(!showOptions)}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"
          >
            {showOptions ? (
              <>
                <ChevronUp className="h-4 w-4" />
                Hide options
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4" />
                Show options
              </>
            )}
          </button>

          {/* Expanded options */}
          {showOptions && (
            <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50/50 p-4">
              {/* Agent selector */}
              <div>
                <label
                  htmlFor="launch-agent"
                  className="mb-1.5 block text-sm font-medium text-slate-700"
                >
                  Agent
                </label>
                <select
                  id="launch-agent"
                  value={agentId}
                  onChange={(e) => setAgentId(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
                >
                  <option value="">Auto / No agent</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name} ({agent.role}){agent.isSystem ? ' [system]' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Runtime */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Runtime</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setRuntime('claude')}
                    className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-all ${
                      runtime === 'claude'
                        ? 'border-slate-900 bg-white text-slate-900 ring-1 ring-slate-900'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    Claude
                  </button>
                  <button
                    type="button"
                    onClick={() => setRuntime('codex')}
                    className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-all ${
                      runtime === 'codex'
                        ? 'border-slate-900 bg-white text-slate-900 ring-1 ring-slate-900'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    Codex
                  </button>
                </div>
              </div>

              {/* Model */}
              <div>
                <label
                  htmlFor="launch-model"
                  className="mb-1.5 block text-sm font-medium text-slate-700"
                >
                  Model
                </label>
                <select
                  id="launch-model"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
                >
                  {MODELS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Max Turns */}
              <div>
                <label
                  htmlFor="launch-turns"
                  className="mb-1.5 block text-sm font-medium text-slate-700"
                >
                  Max Turns
                </label>
                <input
                  id="launch-turns"
                  type="number"
                  min={1}
                  max={1000}
                  value={maxTurns}
                  onChange={(e) => setMaxTurns(Number(e.target.value))}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
                />
              </div>

              {/* Permission mode */}
              <div>
                <label
                  htmlFor="launch-perm"
                  className="mb-1.5 block text-sm font-medium text-slate-700"
                >
                  Permission Mode
                </label>
                <select
                  id="launch-perm"
                  value={permissionMode}
                  onChange={(e) => setPermissionMode(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
                >
                  {PERMISSION_MODES.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Cortex enrichment */}
              <label className="flex items-start gap-2.5 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={cortexEnrich}
                  onChange={(e) => setCortexEnrich(e.target.checked)}
                  className="mt-0.5 rounded border-slate-300"
                />
                <span className="flex items-center gap-1.5">
                  <BookOpen className="h-3.5 w-3.5 text-teal-500" />
                  Enrich with Cortex knowledge
                  <span className="rounded bg-teal-50 px-1.5 py-0.5 text-[10px] font-medium text-teal-600">
                    RAG
                  </span>
                </span>
              </label>

              {/* Auto-restart */}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={autoRestart}
                    onChange={(e) => setAutoRestart(e.target.checked)}
                    className="rounded border-slate-300"
                  />
                  Auto-restart on failure
                </label>
                {autoRestart && (
                  <div className="flex items-center gap-1">
                    <label htmlFor="launch-retries" className="text-xs text-slate-500">
                      Retries:
                    </label>
                    <input
                      id="launch-retries"
                      type="number"
                      min={1}
                      max={10}
                      value={maxRetries}
                      onChange={(e) => setMaxRetries(Number(e.target.value))}
                      className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Config summary when options are hidden */}
          {!showOptions && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
              <span className="font-medium text-slate-700">Config:</span>{' '}
              {selectedPresetId && presets?.find((p) => p.id === selectedPresetId)?.name && (
                <>{presets.find((p) => p.id === selectedPresetId)!.name} &middot; </>
              )}
              {MODELS.find((m) => m.value === model)?.label ?? model} &middot; {runtime} &middot;{' '}
              {maxTurns} turns &middot; {permissionMode} &middot;{' '}
              <span className="font-medium text-slate-700">{runLabel}</span>
              {cortexEnrich && <> &middot; Cortex</>}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {spawnMode === SpawnMode.CLI && <Terminal className="h-4 w-4" />}
              {isSubmitting ? 'Launching...' : 'Launch Instance'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
